<template>
  <div>
    <v-card
      class="mx-auto"
      color="transparent"
      elevation="0"
    >
      <v-card
        :style="`z-index: 1; position: sticky; top: ${focusModeToolbarTop()};`"
        color="rgba(255, 255, 255, 0.8)"
      >
        <EditToolbar>
          <template #title-section>
            <v-btn small icon fab class="px-0" text :to="{ name: 'model-offers-list', params: { contentTypeLabel: 'modeles-d-offres' }, query: {} }">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-toolbar-title v-if="!isViewMode">Modifier le modèle d'offre</v-toolbar-title>
            <v-toolbar-title v-else>Voir le modèle d'offre</v-toolbar-title>
          </template>

          <template #desktop-actions>
            <v-btn v-if="!isViewMode" color="primary" :disabled="(hasUuid ? !canPerformAction('EDIT_MODEL_OFFER') : !canPerformAction('CREATE_MODEL_OFFER')) || !canSave" @click="saveModelOffer('draft')">
              Enregistrer
            </v-btn>

            <v-btn v-if="!isViewMode && hasUuid" color="success" :disabled="!canPerformAction('EDIT_MODEL_OFFER') || !canPublish" @click="openDialogByType('modelOffer')">Publier le modèle d'offre</v-btn>

            <v-btn
              v-if="hasUuid"
              color="primary"
              :disabled="hasChanges || isDraft || loading.form"
              @click="convertToOffer"
            >
              Convertir en offre
            </v-btn>

            <v-btn v-if="!isViewMode && hasUuid" :disabled="!canReset" @click="resetModifications">
              Annuler les modifications
            </v-btn>
          </template>
          <template #mobile-actions>
            <v-list-item v-if="!isViewMode" :disabled="(hasUuid ? !canPerformAction('EDIT_MODEL_OFFER') : !canPerformAction('CREATE_MODEL_OFFER')) || !canSave" @click="saveModelOffer('draft')">
              <v-list-item-icon>
                <v-icon>mdi-content-save</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Enregistrer</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="!isViewMode && hasUuid" color="success" :disabled="!canPerformAction('EDIT_MODEL_OFFER') || !canPublish" @click="openDialogByType('modelOffer')">
              <v-list-item-icon>
                <v-icon>mdi-content-save-check</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Publier le modèle d'offre</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="hasUuid" :disabled="hasChanges || isDraft || loading.form" @click="convertToOffer">
              <v-list-item-icon>
                <v-icon>mdi-axis-arrow</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Convertir en offre</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="!isViewMode && hasUuid" :disabled="!canReset" @click="resetModifications">
              <v-list-item-icon>
                <v-icon>mdi-refresh</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Annuler les modifications</v-list-item-title>
            </v-list-item>
          </template>

          <template #after-actions>
            <ActionsContent
              v-if="isViewMode"
              :actions="[activeBoardConfiguration.actions.edit]"
              :item="modelOfferData"
              :content-type-label="'modeles-d-offres'"
              class="ml-2"
            />
            <v-btn
              v-if="!loading.skeleton && Object.keys(mandatoryFields).length && $vuetify.breakpoint.smAndDown"
              color="primary"
              absolute
              right
              fab
              small
              style="top: 62px; z-index: 1000;"
              @click="drawer = true"
            >
              <v-icon>mdi-alert-circle</v-icon>
            </v-btn>
          </template>
        </EditToolbar>

        <v-progress-linear
          v-if="loading.form"
          indeterminate
          color="primary"
        />
      </v-card>
      <v-row
        class="my-8 px-4 px-md-8 flex-column-reverse flex-md-row"
        :class="{ 'mt-12' : focusMode }"
        no-gutters
      >
        <v-col
          cols="12"
          :md="Object.keys(mandatoryFields).length ? 8 : 12"
          class="pa-0"
        >
          <v-card-text class="pa-0">
            <v-form
              v-model="isValidForm"
              class="d-flex flex-column"
              style="gap: 26px;"
            >
              <fieldset
                v-for="(panel, panelIndex) in rawContentType.panelOrder"
                :key="panelIndex"
                class="d-flex flex-column py-4 px-4"
                style="gap: 16px; border-radius: 10px; border: 0; background: white;"
              >
                <legend
                  class="
                    py-2 px-4
                    overline text-h6 rounded-lg
                  "
                  style="background-color: #e0e0e0;"
                >
                  {{ panel }}
                </legend>

                <template
                  v-for="(input, inputIndex) in getPanel(panel)"
                >
                  <div
                    v-if="showField(input.name)"
                    :ref="input.name"
                    :key="inputIndex"
                    class="px-0 px-md-10"
                    style="background: white; border-radius: 10px;"
                  >
                    <v-skeleton-loader
                      v-if="loading.skeleton"
                      type="list-item"
                    />
                    <template
                      v-else
                    >
                      <template
                        v-if="!['convention', 'selectedApplicationMethod'].includes(input.name)"
                      >
                        <component
                          :is="input.component ? input.component : 'GenericField'"
                          v-model="modelOfferData[input.name]"
                          :name="input.name"
                          :data="modelOfferData"
                          :input="input"
                          :field="computedFields[input.name]"
                          :items="getFieldItems(computedFields[input.name])"
                          :options="references"
                          :classes="getInputClasses(computedFields[input.name])"
                          :clearable="!isViewMode"
                          @item="handleItem"
                        />
                      </template>
                      <template
                        v-else-if="input.name === 'convention'"
                      >
                        <GenericField
                          v-model="modelOfferData.convention"
                          :name="input.name"
                          :data="modelOfferData"
                          :items="positionConventions"
                          :input="input"
                          :field="{ ...input, readonly: isViewMode, disabled: isViewMode }"
                          :classes="getInputClasses(computedFields.convention)"
                          :clearable="!isViewMode"
                        />
                      </template>
                      <template
                        v-else-if="input.name === 'selectedApplicationMethod'"
                      >
                        <EntityInput
                          :key="isViewMode"
                          :field="computedFields[input.name]"
                          :selected-entity-uuid="modelOfferData.selectedApplicationMethod"
                          :classes="getInputClasses(computedFields.selectedApplicationMethod)"
                          :read-only="isViewMode"
                          :items="entityManager.applicationMethods || []"
                          @entity-response="setEntityItems('applicationMethods', $event)"
                          @init-entity="initEntity('applicationMethods', $event)"
                          @selected-entity="setSelectedEntity('applicationMethods', $event)"
                        />
                      </template>
                    </template>
                  </div>
                </template>
              </fieldset>
            </v-form>
          </v-card-text>
        </v-col>
        <v-col
          v-if="Object.keys(mandatoryFields).length && !$vuetify.breakpoint.smAndDown"
          cols="4"
          class="pl-4"
        >
          <div
            class="pt-6"
            :style="`position: sticky; top: ${focusModeToolbarTop('mandatoryFields')};`"
          >
            <MandatoryFieldsSection
              :mandatory-fields="mandatoryFields"
              :is-loading="loading.skeleton"
              @scroll-to-field="scrollToField"
            />
          </div>
        </v-col>
      </v-row>
    </v-card>

    <!-- Mobile: Drawer -->
    <v-navigation-drawer
      v-model="drawer"
      app
      temporary
      right
      style="z-index: 1000;"
    >
      <MandatoryFieldsSection
        :mandatory-fields="mandatoryFields"
        :is-loading="loading.skeleton"
        is-flat
        @scroll-to-field="scrollToField"
      />
    </v-navigation-drawer>
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex';

import { requests } from '@/mixins/index';

import GenericField from '@/components/dynamic/form/templates/GenericField.vue';
import ActionsContent from '@/components/dataTable/components/ActionsContent.vue';
import EntityInput from '@/components/forms/EntityInput.vue';
import EditToolbar from '@/components/layouts/components/EditToolbar.vue';
import PositionInput from '@/components/forms/PositionInput.vue';
import MandatoryFieldsSection from '@/components/dataTable/components/MandatoryFieldsSection.vue';

export default {
  components: { GenericField, ActionsContent, EntityInput, EditToolbar, PositionInput, MandatoryFieldsSection },
  data () {
    return {
      drawer: false,
      modelOfferData: {},
      defaultData: {},
      positionConventions: [],
      isValidForm: false,
      extensionPanel: 0,
      loading: {
        skeleton: this.$route.params.mode !== 'creer',
        form: this.$route.params.mode !== 'creer'
      },
      entityManager: {}
    };
  },
  computed: {
    ...mapState(['activeBoardConfiguration', 'user', 'focusMode']),
    ...mapGetters(['canPerformAction', 'hasWorkspaceAccess']),
    hasUuid () {
      return !!this.$route.params.uuid;
    },
    isEditMode () {
      return this.$route.params.mode === 'editer';
    },
    isViewMode () {
      return this.$route.params.mode === 'vue';
    },
    isCreateMode () {
      return this.$route.params.mode === 'creer';
    },
    rawContentType () {
      return {
        ...this.$store.state.rawContentTypes?.ModelOffer,
        fields: {
          ...this.$store.state.rawContentTypes?.ModelOffer?.fields,
          selectedApplicationMethod: this.$store.state.activeBoardConfiguration?.inputs.find(input => input.name === 'selectedApplicationMethod')
        }
      } || null;
    },
    computedFields () {
      if (!this.rawContentType?.fields || typeof this.rawContentType.fields !== 'object') return {};

      const fieldsArray = Object.entries(this.rawContentType.fields).map(([key, field]) => {
        const input = this.inputs.find(i => i.name === key);
        const commonProps = { ...input, ...field };

        if (this.isViewMode) {
          return [key, { ...commonProps, readonly: true, disabled: true }];
        }

        return [key, commonProps];
      });

      return Object.fromEntries(fieldsArray);
    },
    inputs () {
      return this.$store.state.activeBoardConfiguration?.inputs || [];
    },
    references () {
      return this.$store.state.references || {};
    },
    workspace () {
      return this.$store.state.activeWorkspace || {};
    },
    mandatoryFields () {
      if (!this.rawContentType?.panelOrder || !this.modelOfferData) return {};

      const mandatoryFieldsByPanel = {};

      [...this.rawContentType.panelOrder, 'Période et contrat de l\'offre'].forEach(panelName => {
        const panelInputs = this.getPanel(panelName);

        const mandatoryInPanel = panelInputs.reduce((accumulator, input) => {
          const field = this.computedFields[input.name];
          const isMandatory = field && (field.mandatory || input.mandatory);
          const isMissing = !this.modelOfferData[input.name];

          if (isMandatory && isMissing) {
            accumulator.push({ ...field, name: input.name });
          }
          return accumulator;
        }, []);

        // If we found any mandatory fields for this panel, add them to our result object.
        if (mandatoryInPanel.length > 0) {
          mandatoryFieldsByPanel[panelName] = mandatoryInPanel;
        }
      });

      return mandatoryFieldsByPanel;
    },
    isDraft () {
      return ('status' in this.modelOfferData && this.modelOfferData.status === 'draft');
    },
    canPublish () {
      return !this.loading.form && this.isValidForm && this.isDraft;
    },
    canSave () {
      return !this.loading.form && (this.hasChanges && !!this.modelOfferData.name);
    },
    canReset () {
      return !this.loading.form && this.hasChanges;
    },
    hasChanges () {
      const replacer = (key, value) => (value === '' ? null : value);
      return JSON.stringify(this.modelOfferData, replacer) !== JSON.stringify(this.defaultData, replacer);
    },
    focusModeToolbarTop () {
      return (section = null) => {
        if (this.$vuetify.breakpoint.xsOnly) {
          return '64px';
        }

        if (section === 'mandatoryFields') {
          return this.focusMode ? '112px' : '150px';
        }

        return this.focusMode ? '24px' : '88px';
      };
    }
  },
  watch: {
    inputs: {
      handler () {
        this.modelOfferData = this.initializeData();
        this.defaultData = { ...this.modelOfferData };
      },
      deep: true,
      immediate: true
    }
  },
  async mounted () {
    if (this.hasUuid) await this.getModelOfferData();
  },
  methods: {
    ...mapMutations(['setLoading']),
    async getModelOfferData () {
      this.setComponentLoading(['skeleton', 'form'], true);

      const response = await requests.getModelOffer(this.$route.params.uuid);

      if (!response.success || !response.data) {
        this.$store.commit('setSnackbar', { text: 'Contenu introuvable', color: 'error' });
        this.$router.push({ name: 'model-offers-list', params: { contentTypeLabel: 'modeles-d-offres' } });

        return;
      }

      const { marque, stationCity, tradeUnit, offerFAQs, workspace, applicationMethods, position, convention, ...restData } = response.data;

      const accessType = this.isEditMode ? 'WRITE' : 'READ';
      if (!this.hasWorkspaceAccess(workspace, accessType)) {
        this.$router.push({ name: 'error', params: { errorCode: 401 } });
        return;
      }

      this.entityManager.applicationMethods = applicationMethods;
      this.$store.commit('setActiveWorkspace', workspace);

      this.modelOfferData = {
        position: position?.uuid || '',
        convention: convention?.uuid || '',
        marque: marque?.uuid || '',
        stationCity: stationCity?.uuid || '',
        tradeUnit: tradeUnit?.uuid || '',
        offerFAQs: offerFAQs?.uuid || '',
        ...restData
      };

      this.defaultData = { ...this.modelOfferData };

      this.setComponentLoading(['skeleton', 'form'], false);
    },
    async convertToOffer () {
      this.setComponentLoading('form', true);

      try {
        const { uuid, ...restData } = this.modelOfferData;
        const response = await requests.createOffer({ input: { ...restData, workspace: this.workspace, generatedByModel: true } });

        if (!response.success) {
          this.$store.commit('setSnackbar', { message: response.message, color: 'error' });
          return;
        }

        this.$router.push({ name: 'offer', params: { contentTypeLabel: 'offres', mode: 'editer', uuid: response.data.uuid } });
      } catch (error) {
        this.$store.commit('setSnackbar', { message: error.message, color: 'error' });
      } finally {
        this.setComponentLoading('form', false);
      }
    },
    async saveModelOffer (status = 'draft') {
      this.setComponentLoading('form', true);

      const { configs } = this.rawContentType;

      const response = await requests.saveModelOffer({
        input: {
          ...this.modelOfferData,
          workspace: this.workspace,
          status: status
        },
        isEditMode: this.hasUuid,
        mutationName: this.hasUuid ? configs.update : configs.create
      });

      if (!response.success) {
        // SHOW ERROR MESSAGE
        console.error(response.message);
        this.$store.commit('setSnackbar', { text: 'Une erreur est survenue lors de l\'enregistrement du modèle d\'offre', color: 'error' });
        this.setComponentLoading('form', false);

        return;
      }

      this.$set(this.modelOfferData, 'version', response.data.version);
      this.$set(this.modelOfferData, 'uuid', response.data.uuid);
      this.$set(this.modelOfferData, 'status', response.data.status);

      this.defaultData = { ...this.modelOfferData };

      if (this.hasUuid) {
        this.$store.commit('setSnackbar', { text: status === 'draft' ? 'Enregistrer avec succès.' : 'Publié avec succès.', color: 'success' });
      } else {
        this.$store.commit('setSnackbar', { text: 'Modèle d\'offre créé avec succès.', color: 'success' });
        this.$router.replace({ name: 'model_offer', params: { contentTypeLabel: 'modeles-d-offres', mode: 'editer', uuid: response.data.uuid } });
      }
      this.setComponentLoading('form', false);
    },
    resetModifications () {
      this.getModelOfferData();
    },
    initializeData () {
      return this.inputs.reduce((acc, input) => {
        acc[input.name] = input.defaultValue || null;
        return acc;
      }, {});
    },
    openDialogByType (type = 'modelOffer') {
      switch (type) {
        case 'modelOffer':
          this.$store.commit('setDialog', {
            show: true,
            title: '<p class="col-12 text-center pa-0 mb-0">⚠️ Publication du Modèle d’offre !</p>',
            text: '<p class="text-center">Vous êtes sur le point de publier ce modèle d’offre.<br>Il pourra alors être utilisé par d’autres utilisateurs du workspace centralisé.<br><br>Avant de continuer, assurez-vous que toute information spécifique à votre établissement (nom, adresse, description du site, etc.) a bien été supprimée ou neutralisée.<br>Ces données seront automatiquement ajoutées lors de la diffusion vers les workspaces cibles.<br><strong>Souhaitez-vous continuer ?</strong></p>',
            acceptText: 'Continuer',
            handleAccept: async () => this.saveModelOffer('published')
          });
          break;
        default:
          console.info('Type not found:', type);
          break;
      }
    },
    setEntityItems (entityName, items) {
      this.entityManager[entityName] = items;
    },
    initEntity (entityName, entity) {
      this.modelOfferData[entityName] = entity.uuid;
      if (!this.modelOfferData.selectedApplicationMethod || !this.modelOfferData.applicationDescription || !this.modelOfferData.applicationProcedure) {
        this.setApplicationMethod(entity);
      }
    },
    setSelectedEntity (entityName, entity) {
      this.modelOfferData[entityName] = entity.uuid;
      this.setApplicationMethod(entity);
    },
    setApplicationMethod (item) {
      this.modelOfferData.selectedApplicationMethod = item ? item.uuid : null;
      this.modelOfferData.applicationDescription = item ? item.description || '' : null;
      this.modelOfferData.applicationProcedure = item ? item.procedure || '' : null;
    },
    getInputClasses () {
      if (this.isViewMode) return 'input-color-black';

      return '';
    },
    showField (inputName) {
      switch (inputName) {
        case 'convention': {
          if (this.positionConventions && Object.keys(this.positionConventions).length) {
            return true;
          }

          break;
        }
        default:
          break;
      }

      return (this.computedFields[inputName]);
    },
    getPanel (panel) {
      return this.inputs.filter(input => {
        const computedField = this.computedFields[input.name];
        const panelValue = input?.panel || computedField?.panel;
        return panelValue.includes(panel) ? input : null;
      });
    },
    getFieldItems (field) {
      if (field.optionsByReference && Object.keys(this.references[field.optionsByReference]).length) {
        return Object.entries(this.references[field.optionsByReference]).map(([key, value]) => ({ text: value, value: key }));
      }

      if (field.name === 'selectedApplicationMethod' && this.entityManager.applicationMethods?.length) {
        return this.entityManager.applicationMethods.map(item => ({ text: item.name, value: item.uuid }));
      }

      return [];
    },
    getInputConventions (convention) {
      return [].concat(convention).reduce((acc, item) => {
        if (!item) return acc;

        acc.push({
          value: item.uuid,
          text: item.name
        });
        return acc;
      }, []);
    },
    handlePosition (position, resetConvention = false) {
      // Case 1: The position is cleared.
      if (!position) {
        this.modelOfferData.name = null;
        this.modelOfferData.experience = null;
        this.modelOfferData.convention = null;
        this.positionConventions = [];

        return;
      }

      // Case 2: A new position is selected.
      const { wtitle, experience, convention } = position;

      // Update the job data.
      this.modelOfferData.name = wtitle;
      this.modelOfferData.experience = experience;
      if (resetConvention) this.modelOfferData.convention = null; // Reset convention selection.

      // Update the available conventions for the dropdown.
      this.positionConventions = this.getInputConventions(convention);
    },
    handleItem (item, fieldName) {
      if (fieldName === 'position') {
        this.handlePosition(item, true);
      }
    },
    setComponentLoading (keys, value) {
      [].concat(keys).forEach(key => {
        this.loading[key] = value;
      });
    },
    scrollToField (field) {
      const element = this.$refs[field];

      if (!element) { return; }

      const targetElement = Array.isArray(element) ? element[0] : element;

      this.$vuetify.goTo(targetElement, {
        duration: 500,
        offset: 64 + 16,
        easing: 'easeInOutCubic'
      });

      targetElement.querySelector('input, textarea, select')?.focus();
    },
    goBack () {
      this.$router.go(-1);
    }
  }
};
</script>

<style scoped>
  .v-list-item__subtitle {
    white-space: break-spaces;
  }

  @container edit-toolbar (max-width: 1302px) {
    :deep(.desktop-actions) {
      display: none;
    }

    :deep(.mobile-actions) {
      display: block;
    }
  }
</style>
